import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { UpdateTripPayload } from 'contracts/tracking-v2/trips';
import { useTripStore } from 'pages/tracking-v2/trip-details-page/hooks';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from 'components/ui/dialog';
import { InputGs, TextareaGs } from 'components/ui/Forms';
import { Form, FormField } from 'components/ui/form';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';

type Ref = {
  open: () => void;
  close: () => void;
}

export type UpdateTripVehiclePlateDialogRef = Ref

type Props = {
  onSubmit: (payload: UpdateTripPayload) => void;
  loading?: boolean;
}

export const UpdateTripVehiclePlateDialog = React.forwardRef<Ref, Props>(({
                                                                            onSubmit,
                                                                            loading,
                                                                          }, ref) => {
  const { trip } = useTripStore();
  const [open, setOpen] = React.useState(false);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const vehiclePlateFieldValue = form.watch('vehiclePlate');

  const vehiclePlatesAreSame = React.useMemo(() => {
    return trip?.vehiclePlate === (vehiclePlateFieldValue?.trim() || null);
  }, [trip?.vehiclePlate, vehiclePlateFieldValue]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    if (!trip?.id) return;
    onSubmit({
      tripId: trip.id,
      vehiclePlate: formData.vehiclePlate || null,
      comment: formData.comment || undefined,
    });
  }, [onSubmit, trip?.id]);

  const hydrateForm = React.useCallback(() => {
    form.setValue('vehiclePlate', trip?.vehiclePlate || '');
  }, [trip?.vehiclePlate]);

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
  }, []);

  React.useEffect(() => {
    hydrateForm();
  }, [hydrateForm]);

  React.useImperativeHandle(ref, () => ({
    open: () => onOpenChange(true),
    close: () => onOpenChange(false),
  }), [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'w-full max-w-[540px]'}>
        <DialogHeader className={'mb-6'}>
          <DialogTitle>Alterar placa do veículo</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
            <div className={'flex flex-col gap-4'}>
              <FormField
                control={form.control}
                name={'vehiclePlate'}
                render={({ field }) => (
                  <InputGs {...field} type="text" label={'Placa do veículo'} />
                )}
              />
              <FormField
                control={form.control}
                name={'comment'}
                render={({ field }) => (
                  <TextareaGs
                    {...field}
                    label={'Comentário'}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div className={'grid grid-cols-2 gap-4 mt-6'}>
              <Button.Root
                type={'button'}
                size={'sm'}
                variant={'secondary'}
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button.Root>
              <Button.Root
                type={'submit'}
                size={'sm'}
                disabled={loading || vehiclePlatesAreSame}
              >
                {loading ? <ActivityIndicator /> : 'Salvar'}
              </Button.Root>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
UpdateTripVehiclePlateDialog.displayName = 'UpdateTripVehiclePlateDialog';